import { useContext, useState } from 'react'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'

import {
  useRequestAutoArticleCreationMutation,
  useUpdateMediaActionMutation
} from '@/common/api/modules/mediaAction'
import {
  COOLDOWN_DAYS,
  buildActionTargetUrl,
  sourceAnalysisLabel
} from '@/common/app/modules/media/mediaAction'
import { mediaAnalysisResultPath } from '@/common/app/path'
import { MediaAction, MediaActionStatus } from '@/common/model/media/mediaAction'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import PriorityBadge from '@/components/view/atoms/badge/PriorityBadge'
import AiButton from '@/components/view/atoms/button/AiButton'
import Button from '@/components/view/atoms/button/Button'
import GroupButton from '@/components/view/atoms/button/GroupButton'
import TextButton from '@/components/view/atoms/button/TextButton'
import Heading from '@/components/view/atoms/heading/Heading'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import IconText from '@/components/view/atoms/icon/IconText'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import ContentSideFrame from '@/components/view/layouts/page/ContentSideFrame'
import Side from '@/components/view/layouts/page/Side'
import { MessageType } from '@/components/view/molecules/bar/MessageBar'

import { ActionDetailModalContext } from '@/components/container/modal/automationReview/ActionDetailModalProvider'
import { AutoScheduleModalContext } from '@/components/container/modal/automationReview/AutoScheduleModalProvider'
import { ConfirmDialogContext } from '@/components/container/modal/dialog/confirm/ConfirmDialogProvider'
import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

// ---- 日付ユーティリティ（ローカルTZで扱う。'YYYY-MM-DD' 文字列ベース） ----
const pad = (n: number) => String(n).padStart(2, '0')
const fmtLocal = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const parseLocal = (s: string) => {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d)
}
const addDays = (s: string, n: number) => {
  const d = parseLocal(s)
  d.setDate(d.getDate() + n)
  return fmtLocal(d)
}
// 週の起点（月曜）
const weekMonday = (s: string) => {
  const d = parseLocal(s)
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  return fmtLocal(d)
}
const DAY_LABELS = ['日', '月', '火', '水', '木', '金', '土']

const PERIODS = [
  { key: 'this_month', label: '当月', monthsAhead: 0 },
  { key: 'this_next_month', label: '当・翌月', monthsAhead: 1 },
  { key: 'quarter', label: '四半期', monthsAhead: 2 },
  { key: 'half', label: '半年', monthsAhead: 5 }
] as const
type PeriodKey = (typeof PERIODS)[number]['key']

const priorityStyle = (priority: 'high' | 'medium' | 'low') => {
  switch (priority) {
    case 'high':
      return {
        card: 'border-critical',
        strip: 'bg-fill-critical',
        stripText: TextColor.ON_BG_FILL,
        label: '高'
      }
    case 'medium':
      return {
        card: 'border-primitive-kaki-300',
        strip: 'bg-primitive-kaki-300',
        stripText: TextColor.PRIMARY,
        label: '中'
      }
    default:
      return {
        card: 'border-primary',
        strip: 'bg-sumi-300',
        stripText: TextColor.PRIMARY,
        label: '低'
      }
  }
}

// ロードマップ上のアクションカード（未割当プール・週セルの両方で使う。ドラッグ可能）
const RoadmapActionCard = ({
  siteId,
  action,
  inPool,
  onUnassign,
  onChanged
}: {
  siteId: number
  action: MediaAction
  inPool: boolean
  onUnassign(): void
  onChanged(): void
}) => {
  const navigate = useNavigate()
  const { showModal: showDetail } = useContext(ActionDetailModalContext)
  const { showModal: showConfirm, hide: hideConfirm } = useContext(ConfirmDialogContext)
  const { trigger: requestAutoArticleCreation, isMutating: requesting } =
    useRequestAutoArticleCreationMutation({
      onSuccess: () => {
        hideConfirm()
        onChanged()
      }
    })

  const handleAutoCreate = () =>
    showConfirm({
      message:
        'この推奨アクションから記事の自動作成を予約します。バックグラウンドで数分以内に処理され、他の操作は完了までできなくなります。よろしいですか？',
      noText: 'キャンセル',
      yesText: '自動記事作成を予約',
      onClickYes: async () => {
        await requestAutoArticleCreation({ site_id: siteId, action_id: action.id })
      }
    })

  const handleOpenTargetScreen = () => {
    const url = buildActionTargetUrl(action, siteId)
    if (url) navigate(url)
  }

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(action.id))
        e.dataTransfer.effectAllowed = 'move'
      }}
      className={clsx(
        'bg-surface flex max-w-[400px] cursor-grab flex-col gap-1.5 rounded-lg border p-2.5 active:cursor-grabbing',
        priorityStyle(action.priority).card
      )}
    >
      <div className='flex flex-wrap items-center gap-1.5'>
        <PriorityBadge priority={action.priority} />
        {action.source_analysis_type && action.source_history_id && (
          <TextButton
            size={12}
            newTab
            link={mediaAnalysisResultPath(
              siteId,
              action.source_analysis_type,
              action.source_history_id
            )}
          >
            {sourceAnalysisLabel(action.source_analysis_type)}
          </TextButton>
        )}
        {action.status === 'in_progress' && (
          <Badge size={10} badgeType={BadgeType.DEFAULT}>
            進行中
          </Badge>
        )}
      </div>
      <Text size={12} weight='bold' lineHeight={150}>
        {action.title}
      </Text>
      <div className='flex flex-wrap items-center gap-2'>
        {action.action_type === 'create_article' &&
          (action.auto_article_creation_requested ? (
            <Button disabled>自動記事作成実行中</Button>
          ) : (
            <>
              <AiButton onClick={handleAutoCreate} loading={requesting}>
                自動記事作成
              </AiButton>
              <TextButton size={12} onClick={handleOpenTargetScreen} arrow>
                AI記事作成
              </TextButton>
            </>
          ))}
        {action.action_type === 'rewrite_article' && (
          <TextButton size={12} onClick={handleOpenTargetScreen} arrow>
            記事リライト
          </TextButton>
        )}
        {!inPool && (
          <TextButton size={12} onClick={onUnassign}>
            未割当へ戻す
          </TextButton>
        )}
        <TextButton
          size={12}
          onClick={() => showDetail({ siteId, actionId: action.id, onChanged })}
        >
          詳細
        </TextButton>
      </div>
    </div>
  )
}

interface Props {
  siteId: number
  items: MediaAction[]
  onChanged(): void
}

// 実施計画ロードマップ（週単位のドラッグ＆ドロップで予定日を割り当て）
const MediaActionRoadmapView = ({ siteId, items, onChanged }: Props) => {
  const [period, setPeriod] = useState<PeriodKey>('this_next_month')
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)
  const { showModal: showAutoSchedule } = useContext(AutoScheduleModalContext)
  const { addMessage } = useContext(ToastMessageContext)
  const { trigger: updateAction } = useUpdateMediaActionMutation()

  const todayStr = fmtLocal(new Date())
  const thisWeek = weekMonday(todayStr)
  const thisWeekEnd = addDays(thisWeek, 6)
  const monthsAhead = PERIODS.find((p) => p.key === period)?.monthsAhead ?? 1
  const nowD = parseLocal(todayStr)
  const horizonEnd = fmtLocal(new Date(nowD.getFullYear(), nowD.getMonth() + monthsAhead + 1, 0))

  const unscheduled = items.filter((a) => !a.scheduled_date)
  const scheduled = items.filter((a) => a.scheduled_date)

  // 統計サマリー
  const overdueCount = scheduled.filter(
    (a) => (a.scheduled_date as string).slice(0, 10) < todayStr
  ).length
  const thisWeekCount = scheduled.filter((a) => {
    const d = (a.scheduled_date as string).slice(0, 10)
    return d >= thisWeek && d <= thisWeekEnd
  }).length
  const laterCount = scheduled.filter(
    (a) => (a.scheduled_date as string).slice(0, 10) > thisWeekEnd
  ).length

  // 日付ごとのアクション
  const byDate: Record<string, MediaAction[]> = {}
  scheduled.forEach((a) => {
    const d = (a.scheduled_date as string).slice(0, 10)
    if (!byDate[d]) byDate[d] = []
    byDate[d].push(a)
  })

  // 表示する週（今週〜期間末 ＋ 予定のある過去週）を月曜起点で列挙
  const scheduledWeeks = new Set(scheduled.map((a) => weekMonday(a.scheduled_date as string)))
  const pastWeeks = [...scheduledWeeks].filter((w) => w < thisWeek)
  const weeks: string[] = [...pastWeeks].sort()
  for (let cursor = thisWeek; cursor <= horizonEnd; cursor = addDays(cursor, 7)) weeks.push(cursor)

  // ドロップ：週へ移動（予定日は週の最終日＝日曜）／未割当へ移動
  const moveToWeek = (id: number, weekStart: string) => {
    const sunday = addDays(weekStart, 6)
    updateAction({ site_id: siteId, action_id: id, scheduled_date: sunday })
      .then(() => {
        addMessage({ text: `予定日を ${sunday} に変更しました`, type: MessageType.SUCCESS })
        onChanged()
      })
      .catch(() => undefined)
  }
  const moveToUnassigned = (id: number) => {
    updateAction({ site_id: siteId, action_id: id, scheduled_date: '' })
      .then(() => {
        addMessage({ text: '未割当に戻しました', type: MessageType.SUCCESS })
        onChanged()
      })
      .catch(() => undefined)
  }

  // この週のタスクを一括開始：提案中のアクションを進行中へ切り替える
  const startWeekTasks = (wkActions: MediaAction[]) => {
    const targets = wkActions.filter((a) => a.status === MediaActionStatus.SUGGESTED)
    if (targets.length === 0) return
    Promise.all(
      targets.map((a) =>
        updateAction({
          site_id: siteId,
          action_id: a.id,
          status: MediaActionStatus.IN_PROGRESS
        })
      )
    )
      .then(() => {
        addMessage({
          text: `${targets.length} 件のタスクを進行中に切り替えました`,
          type: MessageType.SUCCESS
        })
        onChanged()
      })
      .catch(() => undefined)
  }

  // ドロップゾーン共通ハンドラ
  const dropZone = (key: string, onDropId: (id: number) => void) => ({
    onDragEnter: (e: React.DragEvent) => {
      e.preventDefault()
      setDragOverKey(key)
    },
    onDragOver: (e: React.DragEvent) => e.preventDefault(),
    onDragLeave: (e: React.DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverKey(null)
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault()
      setDragOverKey(null)
      const id = Number(e.dataTransfer.getData('text/plain'))
      if (id) onDropId(id)
    }
  })

  return (
    <div className='flex flex-col'>
      {/* タスク計画サマリ */}
      <>
        <SummaryHeading
          subText={`同キーワード群の連続実施は ${COOLDOWN_DAYS} 日以上空けることを推奨。`}
          subNode={
            <div className='flex flex-wrap items-center gap-2'>
              <GroupButton
                choices={PERIODS.map((p) => ({ value: p.key, label: p.label }))}
                value={period}
                onSelect={(value) => setPeriod(value as PeriodKey)}
              />
              <AiButton
                onClick={() => showAutoSchedule({ siteId, onApplied: onChanged })}
                size='large'
              >
                AIで実行計画を再作成する
              </AiButton>
            </div>
          }
        >
          タスク計画サマリ
        </SummaryHeading>
        <Area bottomMargin={16}>
          <SummaryItemList shadow>
            <SummaryItem>
              <Text size={12} weight='bold'>
                期限切れ
              </Text>
              <Text size={24} weight='bold' color={TextColor.CRITICAL}>
                {overdueCount}
              </Text>
            </SummaryItem>
            <SummaryItem>
              <Text size={12} weight='bold'>
                今週予定
              </Text>
              <Text size={24} weight='bold'>
                {thisWeekCount}
              </Text>
            </SummaryItem>
            <SummaryItem>
              <Text size={12} weight='bold'>
                来週以降予定
              </Text>
              <Text size={24} weight='bold'>
                {laterCount}
              </Text>
            </SummaryItem>
            <SummaryItem>
              <Text size={12} weight='bold'>
                未割当
              </Text>
              <Text size={24} weight='bold'>
                {unscheduled.length}
              </Text>
            </SummaryItem>
          </SummaryItemList>
        </Area>
      </>

      {/* 2カラム: 週別ロードマップ | 未割当プール */}
      <ContentSideFrame>
        {/* 週別ロードマップ */}
        <Content>
          {weeks.map((wkStart) => {
            const wkEnd = addDays(wkStart, 6)
            const wkDays = Array.from({ length: 7 }, (_, i) => addDays(wkStart, i))
            const wkActions = wkDays.flatMap((d) => byDate[d] ?? [])
            const isPast = wkEnd < todayStr

            return (
              <div key={wkStart} {...dropZone(`week-${wkStart}`, (id) => moveToWeek(id, wkStart))}>
                <Area borderColor={dragOverKey === `week-${wkStart}` ? 'emphasis' : undefined}>
                  <Heading
                    rightNode={
                      wkActions.some((action) => action.status == MediaActionStatus.SUGGESTED) ? (
                        <AiButton onClick={() => startWeekTasks(wkActions)}>
                          この週のタスクを一括開始
                        </AiButton>
                      ) : undefined
                    }
                  >
                    {wkStart.replaceAll('-', '/')} 〜 {wkEnd.replaceAll('-', '/')}
                    {isPast && (
                      <span className='ml-2'>
                        <Badge size={10} badgeType={BadgeType.CRITICAL}>
                          期限切れ
                        </Badge>
                      </span>
                    )}
                  </Heading>
                  {/* 曜日・日付の帯（表示のみ。カードは従来どおり配置） */}
                  <div className='bg-secondary mb-2 flex overflow-hidden px-2'>
                    {wkDays.map((d) => {
                      const dayD = parseLocal(d)
                      const dow = dayD.getDay()
                      const isToday = d === todayStr
                      const dayColor =
                        dow === 0
                          ? TextColor.CRITICAL
                          : dow === 6
                            ? TextColor.LINK
                            : isToday
                              ? TextColor.LINK
                              : TextColor.SECONDARY
                      return (
                        <div
                          key={d}
                          className={clsx(
                            'flex-1 px-2 py-1',
                            isToday && 'bg-surface-emphasis',
                            dow === 0 ? 'bg-critical' : dow === 6 ? 'bg-emphasis' : 'bg-secondary'
                          )}
                        >
                          <Text size={12} weight={isToday ? 'bold' : undefined} color={dayColor}>
                            {dayD.getMonth() + 1}/{dayD.getDate()} {DAY_LABELS[dow]}
                          </Text>
                        </div>
                      )
                    })}
                  </div>
                  <AreaContent>
                    {wkActions.length === 0 ? (
                      <ItemAlign justify='center' align='center'>
                        <IconText
                          text='タスクが追加されていません。右側の未割当プールからドラッグ＆ドロップで追加してください。'
                          iconType={IconType.CLOSE_SUB}
                          color={IconColor.SECONDARY}
                          textColor={TextColor.SECONDARY}
                          direction='horizon'
                          textSize={12}
                          lineHeight={150}
                          noBold
                          iconLine
                        />
                      </ItemAlign>
                    ) : (
                      <div className='grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3'>
                        {wkDays
                          .filter((d) => (byDate[d] ?? []).length > 0)
                          .flatMap((d) =>
                            (byDate[d] ?? []).map((a) => (
                              <RoadmapActionCard
                                key={a.id}
                                siteId={siteId}
                                action={a}
                                inPool={false}
                                onUnassign={() => moveToUnassigned(a.id)}
                                onChanged={onChanged}
                              />
                            ))
                          )}
                      </div>
                    )}
                  </AreaContent>
                </Area>
              </div>
            )
          })}
        </Content>

        {/* 未割当プール */}
        <Side>
          <div {...dropZone('pool', moveToUnassigned)}>
            <Area bottomMargin={16} borderColor={dragOverKey === 'pool' ? 'emphasis' : undefined}>
              <Heading>未割当プール</Heading>
              {unscheduled.length === 0 ? (
                <AreaContent>
                  <Text size={12} color={TextColor.SECONDARY} align='center'>
                    すべてのアクションに予定日が割り当て済み
                  </Text>
                </AreaContent>
              ) : (
                <div className='flex max-h-200 flex-col gap-2 overflow-y-auto'>
                  <AreaContent>
                    {unscheduled.map((a) => (
                      <RoadmapActionCard
                        key={a.id}
                        siteId={siteId}
                        action={a}
                        inPool
                        onUnassign={() => moveToUnassigned(a.id)}
                        onChanged={onChanged}
                      />
                    ))}
                  </AreaContent>
                </div>
              )}
            </Area>
          </div>
        </Side>
      </ContentSideFrame>
    </div>
  )
}

export default MediaActionRoadmapView
